import React, { useId } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import './workspace.scss';

function EntityTabLayout({ activeTab, ariaLabel, className, header, onTabChange, tabs, ...rest }) {
  const generatedId = useId();
  const layoutId = `entity-tabs-${generatedId.replace(/:/g, '')}`;
  const panelId = `${layoutId}-panel`;
  const activeTabConfig = tabs.find(tab => tab.id === activeTab);

  const handleTabListKeyDown = event => {
    const tabButtons = Array.from(
      event.currentTarget.querySelectorAll('[role="tab"]:not(:disabled)')
    );
    const currentIndex = tabButtons.indexOf(document.activeElement);
    let nextIndex;

    if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabButtons.length - 1;
    } else if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % tabButtons.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + tabButtons.length) % tabButtons.length;
    } else {
      return;
    }

    event.preventDefault();
    tabButtons[nextIndex].focus();
  };

  return (
    <div className={classnames('entity-tab-layout', className)} {...rest}>
      {header && <div className="entity-tab-layout__header">{header}</div>}
      <div
        aria-label={ariaLabel}
        className="entity-tab-layout__tabs"
        onKeyDown={handleTabListKeyDown}
        role="tablist"
      >
        {tabs.map(tab => {
          const isActive = tab.id === activeTab;
          const tabId = `${layoutId}-tab-${tab.id}`;

          return (
            <button
              aria-controls={panelId}
              aria-selected={isActive}
              className={classnames('entity-tab-layout__tab', {
                'entity-tab-layout__tab--active': isActive
              })}
              disabled={tab.disabled}
              id={tabId}
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              role="tab"
              tabIndex={isActive ? 0 : -1}
              type="button"
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {tabs.length > 0 && (
        <div
          aria-labelledby={activeTabConfig ? `${layoutId}-tab-${activeTabConfig.id}` : undefined}
          className="entity-tab-layout__panel"
          id={panelId}
          role="tabpanel"
          tabIndex="0"
        >
          {activeTabConfig && activeTabConfig.content}
        </div>
      )}
    </div>
  );
}

EntityTabLayout.defaultProps = {
  activeTab: '',
  ariaLabel: 'Entity sections',
  className: '',
  header: null,
  onTabChange: () => {},
  tabs: []
};

EntityTabLayout.propTypes = {
  activeTab: PropTypes.string,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  header: PropTypes.node,
  onTabChange: PropTypes.func,
  tabs: PropTypes.arrayOf(PropTypes.shape({
    content: PropTypes.node,
    disabled: PropTypes.bool,
    id: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired
  }))
};

export { EntityTabLayout };
